import { Grid, InputAdornment } from '@material-ui/core'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import { orgSelectors } from 'ducks/orgs'
import SurchargingNotSupportedWarning from 'elements/cashFlow/SurchargingNotSupportedWarning'
import StripeSurchargingConfigWarning from 'elements/cashFlow/StripeSurchargingConfigWarning'
import SurchargingTooltip from 'elements/cashFlow/SurchargingTooltip'
import { Button, styled, Tooltip, Typography } from 'opensolar-ui'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { BooleanInput, NumberInput, TextInput, useTranslate } from 'react-admin'
import { useForm, useFormState } from 'react-final-form'
import { useSelector } from 'react-redux'
import {
  getDefaultPaymentMethodsForCountry,
  getIsSurchargingAllowed,
  useCashflowPspForDefaultPaymentMethods,
  useGetPaymentMethodChoices,
} from 'util/cashFlow/configuration'
import AcceptedPaymentMethodsField from './AcceptedPaymentMethodsField'

type PropTypes = {
  paymentIndex: number
  businessState: string
  financePartnerId?: number
  stripeCustomerSurchargeConfigured?: boolean
  stripeCustomerSurchargeLoading?: boolean
}

const MilestoneContainer = styled('div')<{ totalMilestones: number; paymentNumber: number }>(
  ({ totalMilestones, paymentNumber }) => ({
    background: 'white',
    padding: '16px 24px',
    border: '1px solid #e7e7e7',
    borderBottomLeftRadius: paymentNumber < totalMilestones ? '0' : '5px',
    borderBottomRightRadius: paymentNumber < totalMilestones ? '0' : '5px',
    borderTopLeftRadius: paymentNumber === 1 ? '5px' : '0',
    borderTopRightRadius: paymentNumber === 1 ? '5px' : '0',
  })
)

const HeaderRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '5px 0',
})

const StyledInfoIcon = styled(InfoOutlined)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.grey[700],
}))

const StyledTextInput = styled(TextInput)({
  margin: '5px 0',
})

const StyledNumberInput = styled(NumberInput)({
  margin: '5px 0',
})

const FlexRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
})

const StyledBooleanInput = styled(BooleanInput)({
  padding: '5px 0',
  '& .MuiFormControlLabel-root': {
    marginRight: 5,
  },
  ' & .OSUI-FormLabel-root': {
    marginBottom: 0,
  },
})

const MilestoneFields: React.FC<PropTypes> = (props) => {
  // if the milestone is the last in the list of milestones and the list of milestones is longer than 1 then the user cannot choose
  // the percentage, min or max and the milestone will always have to have set_value_to_remaining_amount_due = true
  const [mustUseRemainingValue, setMustUseRemainingValue] = useState<boolean>(false)

  const formState = useFormState()
  const form = useForm()
  const translate = useTranslate()
  const allMilestones = formState.values.payment_milestone_configurations
  const paymentMethodChoices = useGetPaymentMethodChoices()
  const cashflowPspForDefaults = useCashflowPspForDefaultPaymentMethods()
  const orgCountry = useSelector(orgSelectors.getOrg)?.country?.iso2
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)

  // human-friendly value that isn't zero-indexed
  const paymentNumber = useMemo(() => {
    return props.paymentIndex + 1
  }, [props.paymentIndex])

  useEffect(() => {
    if (props.financePartnerId) {
      setMustUseRemainingValue(props.paymentIndex + 1 === allMilestones.length)
    } else {
      setMustUseRemainingValue(props.paymentIndex > 0 && props.paymentIndex + 1 === allMilestones.length)
    }
  }, [props.paymentIndex, allMilestones?.length, props.financePartnerId])

  // add country-specific initial payment method values for each milestone if this is a new config that hasn't yet been saved
  useEffect(() => {
    if (!formState.values?.id && orgCountry) {
      formState.values?.payment_milestone_configurations?.forEach((config, i) => {
        let defaultOptions: string[] = []
        if (i === 0) {
          defaultOptions = getDefaultPaymentMethodsForCountry(
            orgCountry,
            true,
            props.businessState,
            cashflowPspForDefaults
          )
        } else {
          defaultOptions = getDefaultPaymentMethodsForCountry(
            orgCountry,
            false,
            props.businessState,
            cashflowPspForDefaults
          )
        }
        form.change(`payment_milestone_configurations[${i}].accepted_payment_methods`, defaultOptions)
      })
    }
  }, [props.businessState, cashflowPspForDefaults, orgCountry, form])

  const onDelete = useCallback(() => {
    let currentMilestones = [...allMilestones]
    currentMilestones.splice(paymentNumber - 1, 1)
    form.change('payment_milestone_configurations', currentMilestones)
  }, [allMilestones])

  const surchargingIsAllowed = useMemo(() => {
    return getIsSurchargingAllowed(orgCountry)
  }, [orgCountry])

  const isRemovedFromPercentageCalcs = useMemo(() => {
    return Boolean(
      formState.values?.payment_milestone_configurations?.[props.paymentIndex]?.remove_from_percentage_calcs
    )
  }, [formState.values])

  const isDeposit = useMemo(() => {
    return Boolean(formState.values?.payment_milestone_configurations?.[props.paymentIndex]?.is_deposit)
  }, [formState.values])

  return (
    <MilestoneContainer totalMilestones={allMilestones.length} paymentNumber={paymentNumber}>
      <HeaderRow>
        <Typography textVariant="h6Bold">Milestone {paymentNumber}</Typography>
        {paymentNumber > 1 && (
          <Button variant="text" onClick={onDelete}>
            <DeleteOutline />
          </Button>
        )}
      </HeaderRow>
      <Grid container>
        <Grid item xs={6}>
          <StyledTextInput
            label={`Milestone ${paymentNumber} Title`}
            variant="outlined"
            fullWidth
            source={`payment_milestone_configurations[${props.paymentIndex}].title`}
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextInput
            label={`Milestone ${paymentNumber} description`}
            variant="outlined"
            fullWidth
            source={`payment_milestone_configurations[${props.paymentIndex}].description`}
          />
        </Grid>
        {props.financePartnerId && (
          <Grid item xs={12}>
            <StyledTextInput
              label="Payee Name (Visible to pros)"
              variant="outlined"
              source={`payment_milestone_configurations[${props.paymentIndex}].paying_party_name`}
              labelPlacement="end"
              fullWidth
            />
            <Typography textVariant="caption2">
              This field will be visible to the pro when we explain who will pay this milestone. We generally just use
              the name of the finance partner.
            </Typography>
          </Grid>
        )}
        {paymentNumber === 1 && !props.financePartnerId && (
          <>
            <Grid item xs={12}>
              <FlexRow>
                <StyledBooleanInput
                  label="Collect payment when proposal is accepted"
                  source={`payment_milestone_configurations[${props.paymentIndex}].is_deposit`}
                  labelPlacement="end"
                />
              </FlexRow>
            </Grid>

            <Grid item xs={12}>
              <FlexRow>
                <StyledBooleanInput
                  label="Exclude amount from other milestone calculations"
                  source={`payment_milestone_configurations[${props.paymentIndex}].remove_from_percentage_calcs`}
                  labelPlacement="end"
                />
                <span>
                  <Tooltip
                    title={translate(
                      'Set a fixed value for the first milestone and remaining amount due is split between milestones by percentage.'
                    )}
                    enterDelay={300}
                  >
                    <StyledInfoIcon />
                  </Tooltip>
                </span>
              </FlexRow>
            </Grid>
            {isDeposit && (
              <Grid item xs={12}>
                <FlexRow>
                  <StyledBooleanInput
                    label="Enable Payment CTA on Proposal prior to acceptance"
                    source={`payment_milestone_configurations[${props.paymentIndex}].enable_payment_cta_on_proposal`}
                    labelPlacement="end"
                  />
                  <Tooltip
                    title={translate(
                      'Enabling the payment CTA on proposals will allow your customer to generate an invoice and process a payment prior to accepting a proposal.'
                    )}
                    enterDelay={300}
                  >
                    <StyledInfoIcon />
                  </Tooltip>
                </FlexRow>
              </Grid>
            )}
          </>
        )}
        {surchargingIsAllowed && (
          <Grid item xs={12}>
            <FlexRow>
              <StyledBooleanInput
                label="Enable Card Surcharging"
                source={`payment_milestone_configurations[${props.paymentIndex}].surcharging_enabled`}
                labelPlacement="end"
              />
              <span>
                <SurchargingTooltip />
              </span>
            </FlexRow>
          </Grid>
        )}

        {surchargingIsAllowed && (
          <Grid item xs={12}>
            <StripeSurchargingConfigWarning
              surchargingEnabled={Boolean(
                formState.values.payment_milestone_configurations[props.paymentIndex].surcharging_enabled
              )}
              isConfigured={props.stripeCustomerSurchargeConfigured}
              isLoading={props.stripeCustomerSurchargeLoading}
            />
          </Grid>
        )}

        {!mustUseRemainingValue && (
          <Grid container spacing={2}>
            {isRemovedFromPercentageCalcs ? (
              <Grid item xs={12}>
                <StyledNumberInput
                  label="Fixed Amount"
                  source={`payment_milestone_configurations[${props.paymentIndex}].fixed_amount`}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                  }}
                  fullWidth
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={4}>
                  <StyledNumberInput
                    variant="outlined"
                    fullWidth
                    label="Amount Due"
                    source={`payment_milestone_configurations[${props.paymentIndex}].percentage`}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <StyledNumberInput
                    variant="outlined"
                    fullWidth
                    label="Min Amount (Optional)"
                    source={`payment_milestone_configurations[${props.paymentIndex}].min_fixed`}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <StyledNumberInput
                    variant="outlined"
                    fullWidth
                    label="Max Amount (Optional)"
                    source={`payment_milestone_configurations[${props.paymentIndex}].max_fixed`}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}
        <StyledNumberInput
          label="Payment Number"
          source={`payment_milestone_configurations[${props.paymentIndex}].payment_number`}
          hidden={true}
          value={paymentNumber}
          style={{ display: 'none' }}
        />
        {!props.financePartnerId && (
          <AcceptedPaymentMethodsField
            paymentIndex={props.paymentIndex}
            paymentMethods={paymentMethodChoices}
            selectedPaymentMethods={
              formState.values.payment_milestone_configurations[props.paymentIndex].accepted_payment_methods
            }
          />
        )}

        <SurchargingNotSupportedWarning
          surchargingEnabled={formState.values.payment_milestone_configurations[props.paymentIndex].surcharging_enabled}
          acceptedPaymentMethods={
            formState.values.payment_milestone_configurations[props.paymentIndex].accepted_payment_methods
          }
          orgCountry={orgCountry}
        />
      </Grid>
    </MilestoneContainer>
  )
}
export default MilestoneFields
